import {
  type CarrierOperator,
  operatorLatLng,
  useCarrierOperator,
} from "@/api/carrier";
import { DetailDrawer } from "@/components/detail-drawer";
import { StatusBadge } from "@/components/status-badge";
import { Button } from "@/components/ui/button";
import { hasHttpStatus } from "@/lib/api-error";
import { operatorDisplayName } from "@/lib/operator-identity";

interface OperatorDetailDialogProps {
  operator: CarrierOperator | null;
  onOpenChange: (open: boolean) => void;
}

export function OperatorDetailDialog({
  operator,
  onOpenChange,
}: OperatorDetailDialogProps) {
  const detail = useCarrierOperator(operator?.id);
  const data = detail.data ?? operator;
  const name = data ? operatorDisplayName(data) : "Operator";
  const location = data ? operatorLatLng(data) : null;

  return (
    <DetailDrawer
      open={!!operator}
      onOpenChange={onOpenChange}
      title={`Operator — ${name}`}
      widthClassName="sm:max-w-2xl"
    >
      {detail.isLoading && (
        <p role="status" className="text-sm text-muted-foreground">
          Loading operator...
        </p>
      )}
      {detail.error && (
        <div role="alert" className="space-y-3">
          <p className="text-sm text-destructive">
            {hasHttpStatus(detail.error, 403)
              ? "You do not have permission to view this operator."
              : "Failed to load operator."}
          </p>
          {!hasHttpStatus(detail.error, 403) && (
            <Button
              type="button"
              variant="outline"
              size="sm-touch"
              onClick={() => detail.refetch()}
            >
              Retry
            </Button>
          )}
        </div>
      )}
      {data && (
        <div className="space-y-5 text-sm">
          <div className="grid gap-3 sm:grid-cols-2">
            <DetailItem
              label="Email"
              value={data.carrierUser?.email ?? "No email"}
            />
            <DetailItem
              label="Phone"
              value={data.carrierUser?.phone ?? "No phone"}
            />
            <DetailItem
              label="Dispatch center"
              value={
                data.dispatchCenter?.name ??
                data.depot?.name ??
                "Unknown Dispatch Center"
              }
            />
            <DetailItem
              label="Vehicle"
              value={
                data.vehicle?.licensePlate ??
                data.vehicle?.code ??
                "No vehicle assigned"
              }
            />
            <div>
              <div className="text-xs font-medium text-muted-foreground">
                Status
              </div>
              <StatusBadge status={data.status} />
            </div>
            <DetailItem
              label="Invite"
              value={
                data.carrierInvite
                  ? data.carrierInvite.acceptedAt
                    ? "Accepted"
                    : `Pending until ${new Date(
                        data.carrierInvite.expiresAt
                      ).toLocaleDateString()}`
                  : "Linked account"
              }
            />
            <DetailItem
              label="Last location"
              value={
                location
                  ? `${location.lat.toFixed(4)}, ${location.lng.toFixed(4)}`
                  : "No location reported"
              }
            />
            <DetailItem
              label="Last update"
              value={
                data.lastLocationTime
                  ? new Date(data.lastLocationTime).toLocaleString()
                  : "No heartbeat reported"
              }
            />
          </div>

          <RecentList
            title="Recent tasks"
            emptyLabel="No recent tasks"
            rows={(data.tasks ?? []).map((task) => ({
              id: task.id,
              label: `${task.type} · ${task.status}`,
            }))}
          />
          <RecentList
            title="Recent trips"
            emptyLabel="No recent trips"
            rows={(data.trips ?? []).map((trip) => ({
              id: trip.id,
              label: trip.status,
            }))}
          />
          <RecentList
            title="Recent shifts"
            emptyLabel="No recent shifts"
            rows={(data.shifts ?? []).map((shift) => ({
              id: shift.id,
              label: `${new Date(shift.date).toLocaleDateString()} · ${
                shift.shiftType
              } · ${shift.status}`,
            }))}
          />
        </div>
      )}
    </DetailDrawer>
  );
}

function DetailItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      <div className="break-words">{value}</div>
    </div>
  );
}

function RecentList({
  title,
  rows,
  emptyLabel,
}: {
  title: string;
  rows: Array<{ id: string; label: string }>;
  emptyLabel: string;
}) {
  return (
    <div>
      <h3 className="mb-2 text-sm font-medium">{title}</h3>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">{emptyLabel}</p>
      ) : (
        <ul className="space-y-1">
          {rows.map((row) => (
            <li key={row.id} className="rounded-md border px-3 py-2">
              {row.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
